import { type InputHTMLAttributes, useEffect, useId, useMemo, useRef, useState } from 'react'

import i18n from './i18n'
import css from './index.module.css'

import { State } from '#/global/component/Form/State'
import type { DefaultProps } from '#/global/component/Form/types'

import { Config } from '#/global/helper/Config'
import { Input as Helper } from '#/global/helper/DekForms/Input'
import { checkFileType, convertFromBytes } from '#/global/helper/File'

interface ErrorState {
    fileType: boolean
    maxFiles: string[]
    maxFileSize: string[]
    maxTotalSize: string[]
}

type InputProps = InputHTMLAttributes<HTMLInputElement>

interface Props extends DefaultProps, Omit<InputProps, 'onChange' | 'size' | 'type' | 'value' | 'defaultValue'> {
    /**
     * If true, the maximum number of files is calculated based on the maximum total size and the maximum size of a single file
     */
    calculateMaxFiles?: boolean
    dataTransfer: DataTransfer
    hideFileList?: boolean
    maxFiles?: number
    /**
     * Maximum size of a single file in bytes
     */
    maxFileSize?: number
    /**
     * Maximum total size of all files in bytes
     */
    maxTotalSize?: number
    onChange?: (value: DataTransfer) => void
}
const ERROR_STATE_DEFAULT: ErrorState = {
    fileType: false,
    maxFiles: [],
    maxFileSize: [],
    maxTotalSize: [],
}

const MAX_FILE_SIZE = 10 * 1024 * 1024

export const Upload = ({
    calculateMaxFiles = false,
    children,
    className,
    dataTransfer,
    hideFileList = false,
    maxFiles,
    maxFileSize,
    maxTotalSize,
    onChange,
    size,
    state,
    ...props
}: Props) => {
    const { accept, disabled, multiple } = props

    const [error, setError] = useState(ERROR_STATE_DEFAULT)
    const id = useId()
    const input = useRef<HTMLInputElement>(null)
    const limits = useMemo(() => calculateLimits(maxFiles, maxFileSize, maxTotalSize, calculateMaxFiles), [maxFiles, maxFileSize, maxTotalSize, calculateMaxFiles])

    // Udržuje input synchronizovaný s daty
    useEffect(() => {
        if (input.current) {
            input.current.files = dataTransfer.files.length > 0 ? dataTransfer.files : null
        }
    }, [dataTransfer])

    const getDataTransferCopy = () => {
        const dt = new DataTransfer()
        for (let index = 0; index < dataTransfer.files.length; index++) {
            dt.items.add(dataTransfer.files[index])
        }
        return dt
    }

    const handleChange = () => {
        if (!input.current || !input.current.files) {
            return
        }

        const errorState = structuredClone(ERROR_STATE_DEFAULT)
        const dataTransfer = new DataTransfer()

        if (multiple) {
            for (const file of input.current.files) {
                if (accept && !checkFileType(file, accept)) {
                    errorState.fileType = true
                    continue
                }
                if (maxFiles && dataTransfer.files.length >= maxFiles) {
                    errorState.maxFiles.push(file.name)
                    continue
                }
                if (maxFileSize && file.size > maxFileSize) {
                    errorState.maxFileSize.push(file.name)
                    continue
                }
                const totalSize = [...dataTransfer.files].reduce((accumulator, current) => accumulator + current.size, 0)
                if (totalSize + file.size > limits.maxTotalSize) {
                    errorState.maxTotalSize.push(file.name)
                    continue
                }
                dataTransfer.items.add(file)
            }
        } else {
            const file = [...input.current.files].at(-1)!
            if (accept && !checkFileType(file, accept)) {
                errorState.fileType = true
            } else if (maxFileSize && file.size > maxFileSize) {
                errorState.maxFileSize.push(file.name)
            } else {
                dataTransfer.items.clear()
                dataTransfer.items.add(file)
            }
        }

        setError(errorState)
        onChange?.(dataTransfer)
        input.current.files = dataTransfer.files
    }

    const handleDelete = (index: number) => {
        if (disabled) {
            return
        }
        const fileList = getDataTransferCopy()
        fileList.items.remove(index)
        onChange?.(fileList)
    }

    const formWidth = Helper.getFormSize(size)

    const classNameList = [className, formWidth]
    state && classNameList.push(state.level)

    const t = i18n[Config.getLang()]

    return (
        <div className={classNameList.join(' ')}>

            {/* label nad buttonem */}
            {children !== undefined && <label htmlFor={id}>{children}</label>}

            {/* samotný input */}
            <input
                id={id}
                onChange={handleChange}
                ref={input}
                role="upload"
                type="file"
                {...props}
            />

            {/* button na který se kliká */}
            <label
                className={`dek-button-white form__label ${css.button}`}
                htmlFor={id}
                role="button">
                {props.multiple ? t.chooseFiles : t.chooseFile}
            </label>

            {/* Výpis seznamu souborů */}
            {!hideFileList && (
                <div className={`form__upload-files ${formWidth}`.trim()}>
                    {[...dataTransfer.files].map((file, index) => (
                        <div key={index} className="form__upload-files__file">
                            <div className="form__upload-files__name">{file.name}</div>
                            <div className="form__upload-files__delete" onClick={() => handleDelete(index)}>
                                <img src="https://cdn1.idek.cz/file/icon-cancel-33c97f2e.svg" width={16} height={16} />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* State */}
            {state && state.message && (<State formWidth={formWidth} state={state} />)}

            {/* Výpis limitů */}
            <div className={formWidth}>
                {accept && (
                    <div className={error.fileType ? 'input-error' : 'input-info'}>
                        {t.limits.accept(accept)}
                    </div>
                )}
                {limits.maxFiles !== undefined && multiple && (
                    <div className={error.maxFiles.length > 0 ? 'input-error' : 'input-info'}>
                        {t.limits.maxFiles(limits.maxFiles)}
                    </div>
                )}
                {limits.maxFileSize !== undefined && (
                    <div className={error.maxFileSize.length > 0 ? 'input-error' : 'input-info'}>
                        {t.limits.maxFileSize(convertFromBytes(limits.maxFileSize))}
                    </div>
                )}
                {multiple && limits.maxTotalSize !== undefined && (
                    <div className={error.maxTotalSize.length > 0 ? 'input-error' : 'input-info'}>
                        {t.limits.maxTotalSize(convertFromBytes(limits.maxTotalSize))}
                    </div>
                )}
            </div>
        </div>
    )
}

const calculateLimits = (maxFiles?: number, maxFileSize: number = MAX_FILE_SIZE, maxTotalSize?: number, calculateMaxFiles: boolean = false) => {
    if (maxTotalSize === undefined) {
        maxTotalSize = maxFiles === undefined ? MAX_FILE_SIZE : maxFiles * maxFileSize
    }
    if (maxFiles === undefined && calculateMaxFiles) {
        maxFiles = Math.floor(maxTotalSize / maxFileSize)
    }
    return {
        maxFiles,
        maxFileSize,
        maxTotalSize,
    }
}
